import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboardingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { LogOut, Clock, CheckCircle, XCircle, Loader2, ExternalLink, ShieldCheck, AlertTriangle, Building2, User, CircleDot, Circle, Banknote, CreditCard, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2c2cfe83-306e-4fb5-b370-367ad0387383/artifacts/k0uivc6c_4skr1hVuGATobkW3-light.png";

export default function Dashboard() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        const fetchStatusAndRefresh = async () => {
            try {
                // First, try to refresh RyftPay status from their API
                setRefreshing(true);
                try {
                    await onboardingAPI.refreshRyftStatus();
                } catch (refreshErr) {
                    // Silently fail - we'll still show the cached status
                    console.log('RyftPay status refresh skipped:', refreshErr.message);
                }
                setRefreshing(false);
                
                // Then fetch the (potentially updated) application status
                const response = await onboardingAPI.getStatus();
                setApplication(response.data);
            } catch (err) {
                if (err.response?.status === 404) {
                    // No application yet, redirect to onboarding
                    navigate('/onboarding');
                } else {
                    setError('Failed to load application status');
                }
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        };

        fetchStatusAndRefresh();
    }, [navigate]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Manual refresh function
    const handleManualRefresh = async () => {
        setRefreshing(true);
        try {
            const refreshResult = await onboardingAPI.refreshRyftStatus();
            if (refreshResult.data.updated) {
                // Status changed - fetch updated application
                const response = await onboardingAPI.getStatus();
                setApplication(response.data);
            }
        } catch (err) {
            console.error('Failed to refresh status:', err);
        } finally {
            setRefreshing(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Approved</Badge>;
            case 'rejected':
                return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Rejected</Badge>;
            default:
                return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pending Review</Badge>;
        }
    };

    const getRyftStatusBadge = (status) => {
        if (!status) {
            return <Badge variant="outline" className="text-muted-foreground">Not Started</Badge>;
        }
        switch (status) {
            case 'verified':
                return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Verified</Badge>;
            case 'pending_verification':
                return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Pending Verification</Badge>;
            case 'verification_required':
                return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Verification Required</Badge>;
            case 'verification_failed':
                return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Verification Failed</Badge>;
            case 'link_generated':
                return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Action Required</Badge>;
            case 'account_created':
                return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Account Created</Badge>;
            case 'creation_failed':
                return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Setup Failed</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    // Determine if this is KYC (Individual) or KYB (Business)
    const isIndividual = (entityType) => ['Individual', 'Sole Trader'].includes(entityType);
    const getVerificationType = (entityType) => isIndividual(entityType) ? 'KYC' : 'KYB';
    const getVerificationLabel = (entityType) => isIndividual(entityType) 
        ? 'Know Your Customer (KYC)' 
        : 'Know Your Business (KYB)';

    // Check if verification is complete
    const isVerificationComplete = (app) => {
        // For businesses, only "Verified" status counts as complete
        // "NotRequired" for businesses means they still need to complete KYB
        const isBusinessEntity = !isIndividual(app?.entity_type);
        
        if (isBusinessEntity) {
            // Businesses must have "Verified" status
            return app?.ryft_status === 'verified' && app?.ryft_verification_status === 'Verified';
        } else {
            // For individuals/sole traders, NotRequired or Verified both count as complete
            return app?.ryft_status === 'verified' || 
                   app?.ryft_verification_status === 'Verified' || 
                   app?.ryft_verification_status === 'NotRequired';
        }
    };

    // Get verification status display
    const getVerificationStatusDisplay = (app) => {
        const status = app?.ryft_verification_status;
        const isBusinessEntity = !isIndividual(app?.entity_type);
        
        if (!status) {
            if (app?.ryft_status === 'verified') return { status: 'Verified', color: 'green' };
            if (app?.ryft_onboarding_url) return { status: 'Action Required', color: 'orange' };
            return { status: 'Pending', color: 'yellow' };
        }
        switch (status) {
            case 'Verified':
                return { status: 'Verified', color: 'green' };
            case 'NotRequired':
                // For businesses, NotRequired means they need to complete KYB
                // For sole traders, it means they can operate without docs initially
                if (isBusinessEntity) {
                    return { status: 'Action Required', color: 'orange' };
                }
                return { status: 'Verified', color: 'green' };
            case 'Pending':
            case 'PendingVerification':
                return { status: 'In Progress', color: 'purple' };
            case 'Required':
                return { status: 'Action Required', color: 'orange' };
            case 'Failed':
                return { status: 'Failed', color: 'red' };
            default:
                return { status: status, color: 'yellow' };
        }
    };

    // Get icon for verification step
    const getStepIcon = (stepId) => {
        switch (stepId) {
            case 'account_setup':
                return ShieldCheck;
            case 'identity_verification':
                return User;
            case 'business_verification':
                return Building2;
            case 'bank_setup':
                return Banknote;
            case 'payout_ready':
                return CreditCard;
            default:
                return CircleDot;
        }
    };

    // Get step status styling
    const getStepStatusStyle = (status) => {
        switch (status) {
            case 'completed':
                return {
                    iconBg: 'bg-green-500/20',
                    iconColor: 'text-green-500',
                    lineColor: 'bg-green-500',
                    textColor: 'text-green-400'
                };
            case 'pending':
                return {
                    iconBg: 'bg-purple-500/20',
                    iconColor: 'text-purple-500',
                    lineColor: 'bg-purple-500',
                    textColor: 'text-purple-400'
                };
            case 'failed':
                return {
                    iconBg: 'bg-red-500/20',
                    iconColor: 'text-red-500',
                    lineColor: 'bg-red-500',
                    textColor: 'text-red-400'
                };
            default: // not_started
                return {
                    iconBg: 'bg-muted/50',
                    iconColor: 'text-muted-foreground',
                    lineColor: 'bg-muted',
                    textColor: 'text-muted-foreground'
                };
        }
    };

    // Calculate completion percentage
    const getCompletionPercentage = (steps) => {
        if (!steps || steps.length === 0) return 0;
        const completed = steps.filter(s => s.status === 'completed').length;
        return Math.round((completed / steps.length) * 100);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-card">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <a href="https://geektickets.ie" className="flex items-center gap-2">
                        <img src={LOGO_URL} alt="GeekTickets" className="h-10" />
                    </a>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">
                            {user?.first_name} {user?.last_name}
                        </span>
                        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="dashboard-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-6 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold font-['Outfit'] mb-2">Organiser Dashboard</h1>
                    <p className="text-muted-foreground">Track your onboarding progress and application status</p>
                </div>

                {error && (
                    <Card className="mb-6 border-destructive bg-destructive/10">
                        <CardContent className="pt-6">
                            <p className="text-destructive">{error}</p>
                        </CardContent>
                    </Card>
                )}

                {application && (
                    <div className="grid gap-6">
                        {/* Application Status Card */}
                        <Card className="bg-card border-border" data-testid="application-status-card">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="font-['Outfit']">{application.organisation_name}</CardTitle>
                                        <CardDescription>Application ID: {application.id}</CardDescription>
                                    </div>
                                    {getStatusBadge(application.status)}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Application Status</h4>
                                            <div className="flex items-center gap-2">
                                                {application.status === 'approved' ? (
                                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                                ) : application.status === 'rejected' ? (
                                                    <XCircle className="w-5 h-5 text-red-500" />
                                                ) : (
                                                    <Clock className="w-5 h-5 text-yellow-500" />
                                                )}
                                                <span className="capitalize">{application.status}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Submitted</h4>
                                            <p>{new Date(application.created_at).toLocaleDateString('en-IE', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric'
                                            })}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="text-sm font-medium text-muted-foreground mb-1">
                                                {getVerificationType(application.entity_type)} Verification Status
                                            </h4>
                                            <div className="flex items-center gap-3">
                                                {(() => {
                                                    const verificationDisplay = getVerificationStatusDisplay(application);
                                                    const colorClasses = {
                                                        green: 'bg-green-500/20 text-green-400 border-green-500/30',
                                                        purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                                                        orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
                                                        red: 'bg-red-500/20 text-red-400 border-red-500/30',
                                                        yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                                    };
                                                    return <Badge className={colorClasses[verificationDisplay.color]}>{verificationDisplay.status}</Badge>;
                                                })()}
                                                {refreshing && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                                            </div>
                                            {application.ryft_account_id && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    onClick={handleManualRefresh} 
                                                    disabled={refreshing}
                                                    className="mt-2 text-xs"
                                                    data-testid="refresh-status-btn"
                                                >
                                                    <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                                                    Refresh Status
                                                </Button>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-medium text-muted-foreground mb-1">Last Updated</h4>
                                            <p>{new Date(application.updated_at).toLocaleDateString('en-IE', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* KYC/KYB Verification Section - Continue Onboarding with RyftPay */}
                        {application.ryft_onboarding_url && (
                            <Card className="bg-card border-border border-2 border-primary/30" data-testid="kyc-kyb-section">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <ShieldCheck className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <CardTitle className="font-['Outfit'] text-lg">Continue Onboarding with RyftPay</CardTitle>
                                            <CardDescription>
                                                {getVerificationLabel(application.entity_type)} Verification Required
                                            </CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Verification Status Banner */}
                                    {!isVerificationComplete(application) && (
                                        <Alert className="bg-orange-500/10 border-orange-500/30">
                                            <AlertTriangle className="h-4 w-4 text-orange-500" />
                                            <AlertTitle className="text-orange-400">Payouts Not Yet Available</AlertTitle>
                                            <AlertDescription className="text-orange-300/80">
                                                Payouts will not be possible until the {getVerificationType(application.entity_type)} verification process is complete.
                                                Please continue with the verification below.
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {isVerificationComplete(application) && (
                                        <Alert className="bg-green-500/10 border-green-500/30">
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                            <AlertTitle className="text-green-400">Verification Complete</AlertTitle>
                                            <AlertDescription className="text-green-300/80">
                                                Your {getVerificationType(application.entity_type)} verification is complete. You can now receive payouts for your events!
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {/* KYC/KYB Explanation */}
                                    <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                                        <div className="flex items-start gap-3">
                                            {isIndividual(application.entity_type) ? (
                                                <User className="w-5 h-5 text-primary mt-0.5" />
                                            ) : (
                                                <Building2 className="w-5 h-5 text-primary mt-0.5" />
                                            )}
                                            <div>
                                                <h4 className="font-medium mb-1">
                                                    What is {getVerificationType(application.entity_type)}?
                                                </h4>
                                                {isIndividual(application.entity_type) ? (
                                                    <p className="text-sm text-muted-foreground">
                                                        <strong>Know Your Customer (KYC)</strong> is a mandatory anti-money laundering (AML) 
                                                        compliance process used to verify your identity and assess risk. You'll need to provide:
                                                    </p>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">
                                                        <strong>Know Your Business (KYB)</strong> is a mandatory anti-money laundering (AML) 
                                                        compliance process used to verify your business legitimacy. You'll need to provide:
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <ul className="list-disc list-inside text-sm text-muted-foreground ml-8 space-y-1">
                                            {isIndividual(application.entity_type) ? (
                                                <>
                                                    <li>Government-issued photo ID (passport, driving license)</li>
                                                    <li>Proof of address (utility bill, bank statement)</li>
                                                    <li>Bank account details for payouts</li>
                                                </>
                                            ) : (
                                                <>
                                                    <li>Corporate registration documents</li>
                                                    <li>Business ownership structure</li>
                                                    <li>Ultimate Beneficial Owners (UBOs) identification</li>
                                                    <li>Bank account details for payouts</li>
                                                </>
                                            )}
                                        </ul>
                                    </div>

                                    {/* Verification Progress Tracker */}
                                    {application.verification_steps && application.verification_steps.length > 0 && (
                                        <div className="p-4 rounded-lg bg-muted/20 border border-border" data-testid="verification-progress-tracker">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="font-medium">Verification Progress</h4>
                                                <Badge variant="outline" className="text-xs">
                                                    {getCompletionPercentage(application.verification_steps)}% Complete
                                                </Badge>
                                            </div>
                                            
                                            {/* Progress Bar */}
                                            <div className="w-full h-2 bg-muted rounded-full mb-6 overflow-hidden">
                                                <div 
                                                    className="h-full bg-primary transition-all duration-500 rounded-full"
                                                    style={{ width: `${getCompletionPercentage(application.verification_steps)}%` }}
                                                />
                                            </div>
                                            
                                            {/* Steps */}
                                            <div className="space-y-3">
                                                {application.verification_steps.map((step, index) => {
                                                    const StepIcon = getStepIcon(step.id);
                                                    const style = getStepStatusStyle(step.status);
                                                    const isLast = index === application.verification_steps.length - 1;
                                                    
                                                    // Determine if we should show action button for this step
                                                    const showDirectorButton = step.id === 'identity_verification' && 
                                                        step.status === 'not_started' && 
                                                        application.ryft_account_id &&
                                                        application.entity_type && 
                                                        !['Individual', 'Sole Trader'].includes(application.entity_type);
                                                    
                                                    const showBusinessButton = step.id === 'business_verification' && 
                                                        step.status === 'not_started' && 
                                                        application.ryft_account_id;
                                                    
                                                    const showBankAccountButton = step.id === 'bank_setup' && 
                                                        step.status === 'not_started' && 
                                                        application.ryft_account_id &&
                                                        application.ryft_has_accepted_tos;  // Only show after user has visited portal
                                                    
                                                    const dashboardBaseUrl = application.ryft_environment === 'production' 
                                                        ? 'https://dash.ryftpay.com' 
                                                        : 'https://sandbox-dash.ryftpay.com';
                                                    
                                                    return (
                                                        <div key={step.id} className="flex items-start gap-3">
                                                            {/* Step Icon and Line */}
                                                            <div className="flex flex-col items-center">
                                                                <div className={`w-8 h-8 rounded-full ${style.iconBg} flex items-center justify-center`}>
                                                                    {step.status === 'completed' ? (
                                                                        <CheckCircle className={`w-4 h-4 ${style.iconColor}`} />
                                                                    ) : step.status === 'pending' ? (
                                                                        <Loader2 className={`w-4 h-4 ${style.iconColor} animate-spin`} />
                                                                    ) : step.status === 'failed' ? (
                                                                        <XCircle className={`w-4 h-4 ${style.iconColor}`} />
                                                                    ) : (
                                                                        <Circle className={`w-4 h-4 ${style.iconColor}`} />
                                                                    )}
                                                                </div>
                                                                {!isLast && (
                                                                    <div className={`w-0.5 h-6 ${step.status === 'completed' ? 'bg-green-500' : 'bg-muted'}`} />
                                                                )}
                                                            </div>
                                                            
                                                            {/* Step Content */}
                                                            <div className="flex-1 pb-2">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`font-medium text-sm ${step.status === 'completed' ? 'text-green-400' : step.status === 'pending' ? 'text-purple-400' : step.status === 'failed' ? 'text-red-400' : 'text-foreground'}`}>
                                                                            {step.label}
                                                                        </span>
                                                                        {step.status === 'completed' && (
                                                                            <Badge className="bg-green-500/20 text-green-400 text-xs px-1.5 py-0">Done</Badge>
                                                                        )}
                                                                        {step.status === 'pending' && (
                                                                            <Badge className="bg-purple-500/20 text-purple-400 text-xs px-1.5 py-0">In Review</Badge>
                                                                        )}
                                                                        {step.status === 'failed' && (
                                                                            <Badge className="bg-red-500/20 text-red-400 text-xs px-1.5 py-0">Failed</Badge>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* Action Buttons */}
                                                                    {showBankAccountButton && (
                                                                        <Button 
                                                                            variant="outline" 
                                                                            size="sm"
                                                                            className="h-7 text-xs px-2 border-primary/50 text-primary hover:bg-primary/10"
                                                                            onClick={() => window.open(`${dashboardBaseUrl}/settings/banking/add-account`, '_blank')}
                                                                            data-testid="add-bank-account-btn"
                                                                        >
                                                                            Add Bank Account
                                                                        </Button>
                                                                    )}
                                                                    {showDirectorButton && (
                                                                        <Button 
                                                                            variant="outline" 
                                                                            size="sm"
                                                                            className="h-7 text-xs px-2 border-primary/50 text-primary hover:bg-primary/10"
                                                                            onClick={() => window.open(`${dashboardBaseUrl}/settings/individuals/add-individual`, '_blank')}
                                                                            data-testid="add-director-btn"
                                                                        >
                                                                            Add Director Info
                                                                        </Button>
                                                                    )}
                                                                    {showBusinessButton && (
                                                                        <Button 
                                                                            variant="outline" 
                                                                            size="sm"
                                                                            className="h-7 text-xs px-2 border-primary/50 text-primary hover:bg-primary/10"
                                                                            onClick={() => window.open(`${dashboardBaseUrl}/settings/account/edit-docs`, '_blank')}
                                                                            data-testid="add-business-btn"
                                                                        >
                                                                            Add Business Info
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Button Logic:
                                        - "Continue to RyftPay" when user hasn't accepted ToS yet (hasn't visited portal)
                                        - "Login to RyftPay" after user has accepted ToS (has engaged with portal)
                                    */}
                                    {application.ryft_onboarding_url && !application.ryft_has_accepted_tos && (
                                        <>
                                            <Button 
                                                className="w-full bg-primary hover:bg-primary/90 h-12 text-base"
                                                onClick={() => window.open(application.ryft_onboarding_url, '_blank')}
                                                data-testid="continue-to-ryftpay-btn"
                                            >
                                                <ExternalLink className="w-5 h-5 mr-2" />
                                                Continue to RyftPay
                                            </Button>
                                            
                                            <p className="text-xs text-muted-foreground text-center">
                                                You'll be redirected to RyftPay's secure portal to complete verification.
                                                This typically takes 5-10 minutes.
                                            </p>
                                        </>
                                    )}
                                    
                                    {/* Login to RyftPay Button - shows after user has accepted ToS */}
                                    {application.ryft_account_id && application.ryft_has_accepted_tos && (
                                        <>
                                            <Button 
                                                className="w-full bg-primary hover:bg-primary/90 h-12 text-base"
                                                onClick={() => {
                                                    const dashboardUrl = application.ryft_environment === 'production' 
                                                        ? 'https://dash.ryftpay.com' 
                                                        : 'https://sandbox-dash.ryftpay.com';
                                                    window.open(dashboardUrl, '_blank');
                                                }}
                                                data-testid="login-to-ryftpay-btn"
                                            >
                                                <ExternalLink className="w-5 h-5 mr-2" />
                                                Login to RyftPay
                                            </Button>
                                            
                                            <p className="text-xs text-muted-foreground text-center">
                                                Access your RyftPay dashboard to complete verification and manage your account.
                                            </p>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Help Card */}
                        <Card className="bg-muted/30 border-border">
                            <CardContent className="pt-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <ExternalLink className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium mb-1">Need Help?</h4>
                                        <p className="text-sm text-muted-foreground mb-3">
                                            If you have any questions about your application or need assistance, 
                                            our support team is here to help.
                                        </p>
                                        <a 
                                            href="mailto:support@geektickets.ie" 
                                            className="text-sm text-primary hover:underline"
                                        >
                                            Contact Support
                                        </a>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>
        </div>
    );
}
